import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '../query-keys';
import { useLearningApi } from './use-learning-api';
import { UserType } from '../../../services/user-api';
import { useErrorContext } from '../../../components/error';
import { QuizSession, PatchQuizSessionParams } from '../../../services/learning-api';
import { PutQuizSessionProfileRespBody } from '../../../services/learning-api/schema';

export const useGetQuizSession = (
  userType?: UserType,
  department?: string,
  roleDepartmentId?: string
) => {
  const learningApi = useLearningApi();
  return useQuery<QuizSession>({
    queryKey: QUERY_KEYS.learning.quizSession({ userType, department, roleDepartmentId }),
    queryFn: () => learningApi.quizSession.getQuizSession(userType, department, roleDepartmentId),
    enabled: !!userType && !!department,
  });
};

export const useGetHistory = () => {
  const learningApi = useLearningApi();
  return useQuery<QuizSession[]>({
    queryKey: QUERY_KEYS.learning.history(),
    queryFn: () => learningApi.quizSession.getHistory(),
  });
};

export const usePatchQuizSession = (
  userType?: UserType,
  department?: string,
  roleDepartmentId?: string,
  options?: {
    onSuccess?: (updated: QuizSession) => void;
    onError?: (error: any) => void;
  }
) => {
  const queryClient = useQueryClient();
  const learningApi = useLearningApi();
  const { setError } = useErrorContext();
  return useMutation({
    mutationFn: async (params: PatchQuizSessionParams): Promise<QuizSession> =>
      learningApi.quizSession.patchQuizSession(params, userType, department, roleDepartmentId),
    onSuccess: (updated: QuizSession) => {
      queryClient.setQueryData(
        QUERY_KEYS.learning.quizSession({ userType, department, roleDepartmentId }),
        updated
      );
      options?.onSuccess?.(updated);
    },
    onError: (error) => {
      setError({
        code: 500,
        message: error.message,
      });
      options?.onError?.(error);
    },
  });
};

export const usePutQuizSessionProfile = (userType?: UserType, department?: string) => {
  const queryClient = useQueryClient();
  const learningApi = useLearningApi();
  const { setError } = useErrorContext();
  return useMutation({
    mutationFn: async (profile: string): Promise<PutQuizSessionProfileRespBody> =>
      learningApi.quizSession.putQuizSessionProfile(profile, userType, department),
    onSuccess: (updated: PutQuizSessionProfileRespBody) => {
      queryClient.setQueryData(
        QUERY_KEYS.learning.roleDepartmentId(),
        updated?.session?.roleDepartment ?? ''
      );
      queryClient.setQueryData(QUERY_KEYS.learning.quizSession({ userType, department }), updated);
    },
    onError: (error) => {
      setError({
        code: 500,
        message: error.message,
      });
    },
  });
};

export const usePutQuizSessionReset = (
  userType?: UserType,
  department?: string,
  roleDepartmentId?: string
) => {
  const queryClient = useQueryClient();
  const learningApi = useLearningApi();
  const { setError } = useErrorContext();
  return useMutation({
    mutationFn: async (): Promise<QuizSession> =>
      learningApi.quizSession.putQuizSessionReset(userType, department),
    onSuccess: (updated: QuizSession) =>
      queryClient.setQueryData(
        QUERY_KEYS.learning.quizSession({ userType, department, roleDepartmentId }),
        updated
      ),
    onError: (error) => {
      setError({
        code: 500,
        message: error.message,
      });
    },
  });
};
